import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import styles from "./styles.module.scss";
import { IKeyValue } from "../../../../@types/key-value.type";

import { IProjectType } from "../../../../@types/project/project.type";
import { useConfirm } from "../../../../hooks/useConfirm";
import { getAllCustomersNoPagination } from "../../../../services/api/customers/CustomersService";
import { getAllAreasIdsAndNames } from "../../../../services/api/areas/AreasService";
import {
  createProject,
  deleteProject,
} from "../../../../services/api/projects/projectService";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getAllCostCentersIdsAndNames } from "../../../../services/api/costCenter/costCenterService";
import usePermission from "../../../../hooks/usePermission";
import { FeatureStatic } from "../../../../@types/statics/feature.static";
import { storageUserGet } from "../../../../storage/storageUser";
import { getAllResources } from "../../../../services/api/resource/resourceService";
import { ICustomField } from "../../../../@types/customField.type";
import { getAllProjectsCustomField } from "../../../../services/api/projectCustomField/projectCustomFieldService";
import { OptionsDynimicFields } from "../../../../components/OptionsDynimicFields";
import { mapFieldTypeOptionsValue } from "../../../../utils/mapFieldTypeOptions";
import { includeCustomFieldstoApi } from "../../adapters/projectCustomFields";
import { validateCustomFields } from "../../../../utils/validateCustomFields";
import { ProjectStatusService } from "../../../../services/api/projectStatus/ProjectStatusService";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";

dayjs.extend(utc);
dayjs.extend(timezone);

const isDateValid = (date?: Date | null): boolean => {
  return date !== null && date !== undefined && !isNaN(date.getTime());
};

const formatDateForForm = (isoDate: string | null) => {
  if (!isoDate) return "";
  const date = dayjs(isoDate).tz("America/Sao_Paulo");
  return date.format("YYYY-MM-DDTHH:mm:ss");
};

const formatDateToISO = (date: Date | null) => {
  return date ? dayjs(date).toISOString() : null;
};

const projectSchema = z
  .object({
    name: z
      .string()
      .min(1, "Nome é obrigatório")
      .max(100, "Nome deve ter no máximo 100 caracteres"),
    costCenterId: z.string().optional(),
    projectStatusId: z.string().optional(),
    customerId: z.string().optional(),
    managerId: z.string().optional(),
    startDate: z
      .string()
      .nullable()
      .optional()
      .refine(
        (val) => {
          if (!val) return true;
          const date = new Date(val);
          return isDateValid(date);
        },
        {
          message: "Data Início inválida",
        }
      ),
    endDate: z
      .string()
      .nullable()
      .optional()
      .refine(
        (val) => {
          if (!val) return true;
          const date = new Date(val);
          return isDateValid(date);
        },
        {
          message: "Data Fim inválida",
        }
      ),
    observations: z
      .string()
      .max(1000, "Observações deve ter no máximo 1000 caracteres")
      .optional(),
    areaId: z.string().optional(),
  })
  .refine(
    (data) => {
      const startDate = data.startDate ? new Date(data.startDate) : null;
      const endDate = data.endDate ? new Date(data.endDate) : null;
      if (startDate && endDate) {
        return endDate >= startDate;
      }
      return true;
    },
    {
      message: "Data Fim deve ser maior ou igual à Data Início",
      path: ["endDate"],
    }
  );

export type projectData = z.infer<typeof projectSchema>;

interface FormProjectProps {
  pageType: "create" | "update";
  projectData?: IProjectType;
}
interface IComboBoxOption {
  key: string;
  text: string;
}

export const FormProjects: React.FC<FormProjectProps> = ({
  pageType,
  projectData,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [costCenterNameOptions, setCostCenterNameOptions] = useState<
    IComboBoxOption[]
  >([]);
  const [customerOptions, setCustomerOptions] = useState<IComboBoxOption[]>([]);
  const [projectsCustomField, setProjectsCustomField] = useState<
    ICustomField[]
  >([]);
  const [managerOptions, setmanagerOptions] = useState<IComboBoxOption[]>([]);
  const [projectStatusOptions, setProjectStatusOptions] = useState<
    IComboBoxOption[]
  >([]);
  const [areaOptions, setAreaOptions] = useState<IComboBoxOption[]>([]);
  const [canEdit, setCanEdit] = useState<boolean>();
  const navigate = useNavigate();
  const permissions = usePermission(FeatureStatic.projects.id);
  const [formDataCustomFields, setFormDataCustomFields] = useState(
    {} as Record<string, any>
  );
  const [errorsFormDataCustomFields, setErrorsFormDataCustomFields] = useState(
    {}
  );
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<projectData>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      name: projectData?.name || "",
      customerId: projectData?.customerId || "",
      projectStatusId: projectData?.statusId || "",
      managerId: projectData?.managerId || "",
      startDate: projectData?.startDate
        ? formatDateForForm(projectData.startDate)
        : null,
      endDate: projectData?.endDate
        ? formatDateForForm(projectData.endDate)
        : null,
      observations: projectData?.observations || "",
      areaId: projectData?.areaId || "",
      costCenterId: projectData?.costCenterId || "",
    },
  });

  useEffect(() => {
    if (
      projectsCustomField.length > 0 &&
      projectData?.customFieldsObject !== undefined
    ) {
      const customFieldsInitialState = projectsCustomField.reduce(
        (acc, field) => {
          let initialValue;
          switch (mapFieldTypeOptionsValue[field.customFieldTypeId]) {
            case "booleano":
              initialValue = "Não";
              break;
            case "data":
              initialValue = "";
              break;
            case "lista":
              initialValue = "";
              break;
            case "textoMultlinhas":
            case "texto":
            default:
              initialValue = "";
          }
          acc[field.name] = projectData.customFieldsObject?.[field.name]
            ? projectData?.customFieldsObject[field.name]
            : initialValue;
          return acc;
        },
        {} as Record<string, any>
      );

      setFormDataCustomFields(() => ({
        ...customFieldsInitialState,
      }));
    }
  }, [projectsCustomField, projectData]);

  useEffect(() => {
    if (projectData) {
      reset({
        name: projectData?.name || "",
        customerId: projectData?.customerId || "",
        startDate: projectData?.startDate
          ? formatDateForForm(projectData.startDate)
          : null,
        endDate: projectData?.endDate
          ? formatDateForForm(projectData.endDate)
          : null,
        observations: projectData?.observations || "",
        areaId: projectData?.areaId || "",
        costCenterId: projectData?.costCenterId || "",
        managerId: projectData?.managerId || "",
      });
    }
  }, [projectData, reset]);

  useEffect(() => {
    if (projectData?.id) {
      setCanEdit(
        permissions.canEdit(
          projectData?.createdByUserId,
          projectData?.managerId
        )
      );
    } else {
      setCanEdit(permissions.canCreate());
    }
    const loadDomainData = async () => {
      try {
        const responseProjectsCustomField = await getAllProjectsCustomField();
        setProjectsCustomField(responseProjectsCustomField.result.items);

        const responseCostCenterName =
          (await getAllCostCentersIdsAndNames()) as IKeyValue[];
        const responseCustomers =
          (await getAllCustomersNoPagination()) as IKeyValue[];
        const responseProjectStatus =
          (await ProjectStatusService.getAllProjectStatusIdsAndNames()) as IKeyValue[];
        const responseAreas = (await getAllAreasIdsAndNames()) as IKeyValue[];
        const responseManager = (await getAllResources(
          undefined,
          false
        )) as IKeyValue[];
        const customerOptions = responseCustomers.map(
          (customer: IKeyValue) => ({
            key: customer.key,
            text: customer.value,
          })
        );
        const costCenterNameOptions = responseCostCenterName.map(
          (costCenterName: IKeyValue) => ({
            key: costCenterName.key,
            text: costCenterName.value,
          })
        );
        const managerOptions = responseManager.map(
          (managerName: IKeyValue) => ({
            key: managerName.key,
            text: managerName.value,
          })
        );
        const areaOptions = responseAreas.map((area: IKeyValue) => ({
          key: area.key,
          text: area.value,
        }));
        const responseProjectStatusOptions = responseProjectStatus.map(
          (area: IKeyValue) => ({
            key: area.key,
            text: area.value,
          })
        );
        setCostCenterNameOptions(costCenterNameOptions);
        setCustomerOptions(customerOptions);
        setAreaOptions(areaOptions);
        setmanagerOptions(managerOptions);
        setProjectStatusOptions(responseProjectStatusOptions);
      } catch (error) {
        console.log(error);
      }
    };
    loadDomainData();
  }, []);

  const handleFormSubmit = async (data: any) => {
    const user = storageUserGet();
    const projectToApi: IProjectType = {
      id: projectData?.id,
      name: data.name || null,
      key: projectData?.key,
      statusId: data.projectStatusId || null,
      customerId: data.customerId || null,
      costCenterId: data.costCenterId || null,
      managerId: data.managerId || null,
      startDate: data.startDate
        ? formatDateToISO(
            dayjs(data.startDate).tz("America/Sao_Paulo").toDate()
          )
        : null,
      endDate: data.endDate
        ? formatDateToISO(dayjs(data.endDate).tz("America/Sao_Paulo").toDate())
        : null,
      observations: data.observations || "",
      areaId: data.areaId || null,
    };

    if (projectData?.id) {
      projectToApi.updatedByUserId = user.id;
    } else {
      projectToApi.createdByUserId = user.id;
    }

    setErrorsFormDataCustomFields({});
    const newErrors: any = {};
    projectsCustomField.forEach((field) => {
      const error = validateCustomFields(
        field,
        formDataCustomFields[field.name]
      );

      if (error) {
        newErrors[field?.name] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrorsFormDataCustomFields(newErrors);
      return;
    }

    const projectWithCustomFields = includeCustomFieldstoApi(
      projectToApi,
      formDataCustomFields
    );

    try {
      setLoading(true);
      const response = await createProject(projectWithCustomFields);

      if (response.statusCode === 200) {
        toast.success(`${"Projeto salvo com sucesso!"}`);
        navigate("/projects");
      } else {
        toast.error(
          `Não foi possível salvar o projeto! Tente novamente mais tarde.`
        );
      }
    } catch (error) {
      toast.error(
        `Erro: Não foi possível ${pageType === "update" ? "editar" : "criar"}!`
      );
      console.log(error)
    } finally {
      setLoading(false);
    }
  };

  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const handleOpenModalConfirm = () => setOpenModalConfirm(true);
  const handleCloseModalConfirm = () => setOpenModalConfirm(false);

  const { showConfirm, Confirm } = useConfirm(
    "Tem certeza que deseja excluir o projeto?",
    openModalConfirm,
    handleCloseModalConfirm
  );

  const handleDelete = async () => {
    handleOpenModalConfirm();
    if (!projectData?.id) return;
    const isConfirm = await showConfirm();

    if (isConfirm) {
      try {
        const response = await deleteProject(projectData?.id);
        if (response.statusCode === 200) {
          toast.success("Projeto excluído com sucesso!");
          navigate("/projects");
        } else {
          toast.error("Não foi possível excluir o projeto. Tente novamente.");
        }
      } catch (error) {
        toast.error("Erro ao excluir projeto. Tente novamente.");
        console.log(error)
      }
    }
  };

  return (
    <>
      <form
        className={styles.formProject}
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <h3 className={styles.title}>Informações Gerais</h3>

        <div className={styles.fields}>
          <Controller
            name="name"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                label="Projeto"
                placeholder={
                  pageType === "create"
                    ? "Insira o nome do projeto aqui..."
                    : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                value={field.value}
                onChange={field.onChange}
                error={!!errors.name}
                helperText={errors.name?.message}
                fullWidth
                disabled={!canEdit}
              />
            )}
          />
          <Controller
            name="costCenterId"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                options={costCenterNameOptions}
                getOptionLabel={(option) => option.text}
                value={
                  costCenterNameOptions.find(
                    (option) => option.key === field.value
                  ) || null
                }
                onChange={(_, newValue) => field.onChange(newValue?.key || "")}
                noOptionsText="Sem opções"
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.key}>
                      {option.text}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Centro de Custo "
                    InputLabelProps={{
                      shrink: true,
                    }}
                    placeholder={
                      pageType === "create"
                        ? "Selecione o Centro de Custo aqui..."
                        : ""
                    }
                    error={!!errors.costCenterId}
                    helperText={errors.costCenterId?.message}
                    fullWidth
                  />
                )}
                disabled={!canEdit}
              />
            )}
          />
        </div>

        <div className={styles.fields}>
          <Controller
            name="customerId"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                options={customerOptions}
                getOptionLabel={(option) => option.text}
                value={
                  customerOptions.find(
                    (option) => option.key === field.value
                  ) || null
                }
                onChange={(_, newValue) => field.onChange(newValue?.key || "")}
                noOptionsText="Sem opções"
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.key}>
                      {option.text}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    placeholder={
                      pageType === "create" ? "Selecione o cliente aqui..." : ""
                    }
                    error={!!errors.customerId}
                    helperText={errors.customerId?.message}
                    fullWidth
                  />
                )}
                disabled={!canEdit}
              />
            )}
          />

          <Controller
            name="managerId"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                options={managerOptions}
                getOptionLabel={(option) => option.text}
                value={
                  managerOptions.find((option) => option.key === field.value) ||
                  null
                }
                onChange={(_, newValue) => field.onChange(newValue?.key || "")}
                noOptionsText="Sem opções"
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.key}>
                      {option.text}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Responsável"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    placeholder={
                      pageType === "create"
                        ? "Selecione o responsável aqui..."
                        : ""
                    }
                    error={!!errors.managerId}
                    helperText={errors.managerId?.message}
                    fullWidth
                  />
                )}
                disabled={!canEdit}
              />
            )}
          />

          <Controller
            name="areaId"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                options={areaOptions}
                getOptionLabel={(option) => option.text}
                value={
                  areaOptions.find((option) => option.key === field.value) ||
                  null
                }
                onChange={(_, newValue) => field.onChange(newValue?.key || "")}
                noOptionsText="Sem opções"
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.key}>
                      {option.text}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Área"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    placeholder={
                      pageType === "create" ? "Selecione a área aqui..." : ""
                    }
                    error={!!errors.areaId}
                    helperText={errors.areaId?.message}
                    fullWidth
                  />
                )}
                disabled={!canEdit}
              />
            )}
          />
        </div>

        <div className={styles.fields}>
          <Controller
            name="startDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                label="Data Início"
                value={
                  field.value
                    ? dayjs(field.value).tz("America/Sao_Paulo")
                    : null
                }
                onChange={(newValue) => {
                  field.onChange(
                    newValue ? newValue.startOf("day").toISOString() : null
                  );
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    InputLabelProps: { shrink: true },
                    error: !!errors.startDate,
                    helperText: errors.startDate?.message,
                  },
                }}
                disabled={!canEdit}
              />
            )}
          />

          <Controller
            name="endDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                label="Data Fim"
                value={
                  field.value
                    ? dayjs(field.value).tz("America/Sao_Paulo")
                    : null
                }
                onChange={(newValue) => {
                  field.onChange(
                    newValue ? newValue.startOf("day").toISOString() : null
                  );
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    InputLabelProps: { shrink: true },
                    error: !!errors.endDate,
                    helperText: errors.endDate?.message,
                  },
                }}
                disabled={!canEdit}
              />
            )}
          />

          <Controller
            name="projectStatusId"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                options={projectStatusOptions}
                getOptionLabel={(option) => option.text}
                value={
                  projectStatusOptions.find(
                    (option) => option.key === field.value
                  ) || null
                }
                onChange={(_, newValue) => field.onChange(newValue?.key || "")}
                noOptionsText="Sem opções"
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.key}>
                      {option.text}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Status"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    placeholder={
                      pageType === "create" ? "Selecione o status aqui..." : ""
                    }
                    error={!!errors.projectStatusId}
                    helperText={errors.projectStatusId?.message}
                    fullWidth
                  />
                )}
                disabled={!canEdit}
              />
            )}
          />
        </div>

        <div className={styles.fields}>
          <Controller
            name="observations"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                label="Observações"
                placeholder={
                  pageType === "create" ? "Insira as observações aqui..." : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                value={field.value}
                onChange={field.onChange}
                error={!!errors.observations}
                helperText={errors.observations?.message}
                multiline
                rows={4}
                fullWidth
                disabled={!canEdit}
              />
            )}
          />
        </div>
        <OptionsDynimicFields
          dynamicFields={projectsCustomField}
          formDataCustomFields={formDataCustomFields}
          setFormDataCustomFields={setFormDataCustomFields}
          errorsFormDataCustomFields={errorsFormDataCustomFields}
        />

        <div
          className={`${styles.btnContainer} ${
            pageType === "update" ? styles.updatePage : styles.createPage
          } `}
        >
          {pageType === "update" && (
            <Button
              variant="contained"
              color="secondary"
              type="button"
              onClick={handleDelete}
              className={styles.btnsDeleted}
              disabled={!canEdit}
            >
              Excluir Projeto
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={loading || !canEdit}
            className={styles.btnsSave}
          >
            Salvar
          </Button>
        </div>
      </form>
      {Confirm}
    </>
  );
};
